/**
 * useMembers — admin member-management data layer.
 *
 * Wraps the requireAdminScope-gated endpoints on /api/members:
 *   - useAdminMembers()   GET    /api/members      (full roster incl. archived)
 *   - useCreateMember()   POST   /api/members
 *   - useCreateMemberInvite() POST /api/members/:id/invite
 *   - useCreatePasswordReset() POST /api/members/:id/password-reset (#141)
 *   - useUpdateMember()   PATCH  /api/members/:id
 *   - useArchiveMember()  POST   /api/members/:id/archive
 *   - useUnarchiveMember()POST   /api/members/:id/unarchive
 *
 * The list query is disabled for non-admin sessions (no fetch fired) mirroring
 * useAiUsage's T-ADMIN-LEAK-CACHE guard. Mutations throw `ApiError` so the UI
 * can detect a 403 admin-scope error and trigger the re-auth modal, then retry.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminCreateMemberInput,
  AdminUpdateMemberInput,
  MemberInviteCreateResult,
  MemberAdminRow,
  PasswordResetCreateResult,
} from '@stell/shared';
import { useSession } from '../auth/useSession';
import { ApiError, toApiError } from '../../lib/api';

// Flyttet til lib/api.ts i #141; re-eksportert for eksisterende importer.
export { ApiError, toApiError };

/** True for the 403 errors the admin re-auth modal can recover from. */
export function isAdminScopeError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    (err.code === 'admin_scope_required' || err.code === 'admin_scope_expired')
  );
}

export const ADMIN_MEMBERS_KEY = ['admin', 'members'] as const;

export function useAdminMembers() {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  return useQuery<MemberAdminRow[]>({
    queryKey: ADMIN_MEMBERS_KEY,
    enabled: isAdmin,
    queryFn: async () => {
      const res = await fetch('/api/members', { credentials: 'same-origin' });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { members: MemberAdminRow[] };
      return body.members;
    },
  });
}

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminCreateMemberInput) => {
      const res = await fetch('/api/members', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as { memberId: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_MEMBERS_KEY });
    },
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: AdminUpdateMemberInput }) => {
      const res = await fetch(`/api/members/${args.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args.input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as { memberId: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_MEMBERS_KEY });
    },
  });
}

export function useCreateMemberInvite() {
  return useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`/api/members/${memberId}/invite`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as MemberInviteCreateResult;
    },
  });
}

/** Engangslenke for nytt passord (#141). Logger ikke ut medlemmet. */
export function useCreatePasswordReset() {
  return useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`/api/members/${memberId}/password-reset`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as PasswordResetCreateResult;
    },
  });
}

function useArchiveAction(path: 'archive' | 'unarchive') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/members/${id}/${path}`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as { memberId: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_MEMBERS_KEY });
    },
  });
}

export function useArchiveMember() {
  return useArchiveAction('archive');
}

export function useUnarchiveMember() {
  return useArchiveAction('unarchive');
}
